/**
 * Mutations for get appointment module
 */

import Vue from "vue";
const curDate = new Date();

export const mutations = {
  /*
   * SIMPLE SETTERS
   */
  setYear(state, year) {
    state.selectedYear = year;
    state.selectedWeekYear = year;
  },
  setMonth(state, month) {
    state.selectedMonth = month;
    state.selectedWeekMonth = month;
  },
  setDayOfMonth(state, day) {
    state.selectedDayOfMonth = day;
    state.selectedWeek = day;
  },
  setWeek(state, day) {
    state.selectedWeek = day;
  },
  toggleShowSplitScreenMonthly(state) {
    state.showSplitScreenMonthly = !state.showSplitScreenMonthly;
  },
  openShowSplitScreenMonthly(state) {
    state.showSplitScreenMonthly = true;
  },
  setWeekMonth(state, month) {
    state.selectedWeekMonth = month;
  },
  setWeekYear(state, year) {
    state.selectedWeekYear = year;
  },
  addMonthCoordinate(state, coordinate) {
    state.monthCoordinates[coordinate.month] = coordinate.coordinate;
  },
  increaseLoadingGetAppointment(state) {
    state.loadingGetAppointment++;
  },
  decreaseLoadingGetAppointment(state) {
    state.loadingGetAppointment--;
  },
  toggleShowWeeks(state) {
    state.showWeeks = !state.showWeeks;
  },
  setOrderAndFormatForGetAppointment(
    state,
    { clientNameFormat, clientNameOrder }
  ) {
    state.clientNameFormat = isNaN(clientNameFormat)
      ? 1
      : parseInt(clientNameFormat);
    state.clientNameOrder = isNaN(clientNameOrder)
      ? 1
      : parseInt(clientNameOrder);
  },

  // COMPLEX SETTERS

  /**
   * Concat (merge the current dates array with the given array) the given year as an appointment date
   *
   * @param {Object} state
   * @param {Object} json - format { appointments: ["2020-03-02", "..."] }
   * @param {Number} selectedProviderId
   *
   */
  concatYears(state, { json, selectedProviderId }) {
    if (selectedProviderId in state.appointmentDates) {
      Vue.set(
        state.appointmentDates,
        selectedProviderId,
        [...state.appointmentDates[selectedProviderId]].concat(
          json.appointments
        )
      );
    } else {
      Vue.set(state.appointmentDates, selectedProviderId, json.appointments);
    }
  },

  /**
   * Push a single date to the appointment dates
   *
   * @param {Object} state
   * @param {String} date
   * @param {Number} selectedProviderId
   */
  pushAppointmentYears(state, { date, selectedProviderId }) {
    if (selectedProviderId in state.appointmentDates) {
      if (state.appointmentDates[selectedProviderId].indexOf(date) === -1) {
        Vue.set(state.appointmentDates, selectedProviderId, [
          ...state.appointmentDates[selectedProviderId],
          date
        ]);
      }
    } else {
      Vue.set(state.appointmentDates, selectedProviderId, [date]);
    }
  },

  /**
   * Add the given year to the years already queried array
   *
   * @param {Object} state
   * @param {Number} year
   * @param {Number} selectedProviderId
   */
  yearAdded(state, { year, selectedProviderId }) {
    Vue.set(state.yearsAlreadyQueried, selectedProviderId, [
      ...(state.yearsAlreadyQueried[selectedProviderId] || []),
      year
    ]);
  },

  /**
   * These are dates which require refresh,
   * so these dates are removed from the already queried dates
   *
   * @param {Object} state
   * @param {String} date
   * @param {Number} selectedProviderId
   */
  refreshDate(state, { date, selectedProviderId }) {
    if (selectedProviderId in state.appointmentDatesAlreadyQueried) {
      const index = state.appointmentDatesAlreadyQueried[
        selectedProviderId
      ].indexOf(date);
      if (index !== -1) {
        const newListOfAlreadyQueried = [
          ...state.appointmentDatesAlreadyQueried[selectedProviderId]
        ];
        newListOfAlreadyQueried.splice(index, 1);

        Vue.set(
          state.appointmentDatesAlreadyQueried,
          selectedProviderId,
          newListOfAlreadyQueried
        );
      }
    }
  },

  /**
   * Add a date to the already queried dates
   *
   * @param {Object} state
   * @param {String} date
   * @param {Number} selectedProviderId
   */
  addDateToAlreadyQueriedDetails(state, { date, selectedProviderId }) {
    if (selectedProviderId in state.appointmentDatesAlreadyQueried) {
      if (
        state.appointmentDatesAlreadyQueried[selectedProviderId].indexOf(
          date
        ) === -1
      ) {
        Vue.set(state.appointmentDatesAlreadyQueried, selectedProviderId, [
          ...state.appointmentDatesAlreadyQueried[selectedProviderId],
          date
        ]);
      }
    } else {
      Vue.set(state.appointmentDatesAlreadyQueried, selectedProviderId, [date]);
    }
  },

  /**
   * A function to remove all the dates of the years in the years array
   * and from the also remove these years from the years queried array
   * so that it can be refreshed after delete
   *
   * @param {Object} state
   * @param {Array} years
   */
  refreshYears(state, { years, selectedProviderId }) {
    // Step 1: Remove dates from dates which have appointments array
    if (selectedProviderId in state.appointmentDates) {
      const newAppointmentDates = state.appointmentDates[
        selectedProviderId
      ].filter(date => {
        const year = parseInt(date.split("-")[0]);
        return years.indexOf(year) === -1;
      });

      Vue.set(state.appointmentDates, selectedProviderId, newAppointmentDates);
    }

    // Step 2: Remove year form the years queried array so that it has to be refreshed again
    if (selectedProviderId in state.yearsAlreadyQueried) {
      const newYearsAlreadyQueried = [
        ...state.yearsAlreadyQueried[selectedProviderId]
      ].filter(year => {
        return years.indexOf(year) === -1;
      });

      Vue.set(
        state.yearsAlreadyQueried,
        selectedProviderId,
        newYearsAlreadyQueried
      );
    }
  },

  /**
   * A function to remove deleted appointments from the list of appointemnts
   *
   * @param {Object} state
   */
  removeFromAppointmentList(state, { date, id }) {
    // Step 1: Check if date has already been queried
    if (date in state.appointments) {
      const appointmentList = state.appointments[date].filter(appointment => {
        return appointment.id !== id;
      });

      // Step 2: Actually set vue state for appointments
      Vue.set(state.appointments, date, appointmentList);
    }
  },

  /**
   * A function to set the appointments cancelled
   *
   * @param {Object} state
   */
  cancelFromAppointmentList(state, { date, id, cancelOrUn }) {
    // Step 1: Check if date has already been queried
    if (date in state.appointments) {
      const appointmentList = state.appointments[date].map(appointment => {
        if (appointment.id === id) {
          appointment.is_cancelled = cancelOrUn === "cancel" ? 1 : 0;
        }
        return appointment;
      });

      // Step 2: Actually set vue state for appointments
      Vue.set(state.appointments, date, appointmentList);
    }
  },

  /**
   * Function for adding the currently fetched appointment data to the previously fetched state
   *
   * @param {Object} state
   * @param {Object} localStateAppointments
   */
  updateGlobalAppointmentsWithLocalAppointments(state, localStateAppointments) {
    for (let date of Object.keys(localStateAppointments)) {
      if (!(date in state.appointments)) {
        // Step 1. If the date key is not in the global appointments, simply add it
        Vue.set(state.appointments, date, localStateAppointments[date]);
        continue;
      }

      // Step 1: All appointments that should be present in the list
      let appointments = [
        ...localStateAppointments[date],
        ...state.appointments[date]
      ];

      // Step 2: Filter duplicate elements
      let appointmentDistinctId = [];
      appointments = appointments.filter(appointment => {
        if (appointmentDistinctId.indexOf(appointment.id) === -1) {
          appointmentDistinctId.push(appointment.id);
          return true;
        } else {
          return false;
        }
      });

      // Step 3: Sort the list
      appointments.sort((a, b) => {
        return a["app_start_tt"] - b["app_start_tt"] !== 0
          ? a["app_start_tt"] - b["app_start_tt"]
          : a["app_start_hh"] - b["app_start_hh"] !== 0
          ? a["app_start_hh"] - b["app_start_hh"]
          : a["app_start_mm"] - b["app_start_mm"] !== 0
          ? a["app_start_mm"] - b["app_start_mm"]
          : a["app_end_tt"] - b["app_end_tt"] !== 0
          ? a["app_end_tt"] - b["app_end_tt"]
          : a["app_end_hh"] - b["app_end_hh"] !== 0
          ? a["app_end_hh"] - b["app_end_hh"]
          : a["app_end_mm"] - b["app_end_mm"];
      });

      // Step 4: Setting the vue state
      Vue.set(state.appointments, date, appointments);
    }
  },

  /**
   * Function for when appointment is added and an appointment is beinging added to the list of appointments
   *
   * @param {Object} state
   * @param {Object} appointment
   */
  addOrderedNextAppointmentForDate(
    state,
    { appointment, selectedProviderId, isEdit = false }
  ) {
    // Checking if the an appointment with the same ID has already been added to take care of edge case
    if (
      !(appointment["app_date"] in state.appointments) ||
      state.appointments[appointment["app_date"]].findIndex(currApp => {
        return currApp.id === appointment.id;
      }) === -1
    ) {
      // Step 1 : Checking if appointment is the first appointment or there are appointment
      //  there in the array
      let appointments =
        appointment["app_date"] in state.appointments
          ? [...state.appointments[appointment["app_date"]], appointment]
          : [appointment];

      // Step 2 : Sorting the array by date
      appointments.sort((a, b) => {
        return a["app_start_tt"] - b["app_start_tt"] !== 0
          ? a["app_start_tt"] - b["app_start_tt"]
          : a["app_start_hh"] - b["app_start_hh"] !== 0
          ? a["app_start_hh"] - b["app_start_hh"]
          : a["app_start_mm"] - b["app_start_mm"] !== 0
          ? a["app_start_mm"] - b["app_start_mm"]
          : a["app_end_tt"] - b["app_end_tt"] !== 0
          ? a["app_end_tt"] - b["app_end_tt"]
          : a["app_end_hh"] - b["app_end_hh"] !== 0
          ? a["app_end_hh"] - b["app_end_hh"]
          : a["app_end_mm"] - b["app_end_mm"];
      });

      // Step 3: Setting the vue state
      Vue.set(state.appointments, appointment["app_date"], appointments);
    }

    // Adding appointment date to yearsQueried
    if (!isEdit) {
      if (selectedProviderId in state.appointmentDates) {
        if (
          state.appointmentDates[selectedProviderId].indexOf(
            appointment["app_date"]
          ) === -1
        ) {
          Vue.set(state.appointmentDates, selectedProviderId, [
            ...state.appointmentDates[selectedProviderId],
            appointment["app_date"]
          ]);
        }
      } else {
        Vue.set(state.appointmentDates, selectedProviderId, [
          appointment["app_date"]
        ]);
      }
    }
  },

  /**
   * Restoring defaults
   *
   * @param {Object} state
   */
  restoreAppointmentDefaultsToEmpty(state) {
    state.loadingGetAppointment = 0;
    state.selectedDayOfMonth = curDate.getDate();
    state.selectedWeek = curDate.getDate();
    state.selectedMonth = curDate.getMonth();
    state.selectedWeekMonth = curDate.getMonth();
    state.selectedYear = curDate.getFullYear();
    state.selectedWeekYear = curDate.getFullYear();
    state.appointmentDates = {};
    state.yearsAlreadyQueried = {};
    state.appointments = {};
    state.appointmentDatesAlreadyQueried = {};
    state.monthCoordinates = {};
    state.showWeeks = true;
    state.showSplitScreenMonthly = false;
  }
};
